import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, can, currentUser } from "../lib/api";
import { formatQAR } from "../lib/money";
const cardStyle = {
    background: "var(--surface)",
    border: "1px solid var(--line-soft)",
    borderRadius: 10,
    overflow: "hidden",
    marginBottom: 24,
};
const theadCell = { fontSize: "0.66rem", color: "var(--ink-faint)", textTransform: "uppercase" };
const rowLink = {
    display: "grid",
    gap: 8,
    padding: "10px 18px",
    borderTop: "1px solid var(--line-soft)",
    textDecoration: "none",
    color: "inherit",
    alignItems: "center",
};
const tileDefs = [
    { key: "totalCases", label: "Total Cases", to: "/legal-cases/cases" },
    { key: "openCases", label: "Open Cases", to: "/legal-cases/cases?status=OPEN_EXCL_COURT" },
    { key: "closedCases", label: "Closed Cases", to: "/legal-cases/cases?status=CLOSED_ALL" },
    { key: "courtProceedings", label: "Court Proceedings", to: "/legal-cases/cases?status=COURT_PROCEEDINGS" },
    { key: "overdueFollowUps", label: "Overdue Follow-ups", to: "/legal-cases/cases?overdue=true" },
    { key: "outstandingRecoverable", label: "Outstanding", format: formatQAR, to: "/legal-cases/reports?type=outstanding-recovery" },
    { key: "totalRecovered", label: "Recovery Amount", format: formatQAR, to: "/legal-cases/reports?type=recovery" },
    { key: "recoveryPercentage", label: "Recovery %", format: (n) => `${n.toFixed(2)}%`, to: "/legal-cases/reports?type=recovery" },
];
export function Dashboard() {
    const [summary, setSummary] = useState(null);
    const [byCompany, setByCompany] = useState([]);
    const [byAgeing, setByAgeing] = useState([]);
    const [error, setError] = useState(null);
    useEffect(() => {
        Promise.all([
            api.get("/dashboard"),
            api.get("/dashboard/by-company"),
            api.get("/dashboard/by-ageing"),
        ])
            .then(([s, company, ageing]) => {
            setSummary(s);
            setByCompany(company.items);
            setByAgeing(ageing.items);
        })
            .catch(() => setError("Couldn't load the dashboard."));
    }, []);
    return (<div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 22 }}>
        <div>
          <h1 style={{ fontFamily: "var(--font-display)", fontWeight: 600, fontSize: "1.7rem", margin: 0 }}>
            Legal Overview
          </h1>
          <p style={{ color: "var(--ink-soft)", margin: "4px 0 0", fontSize: "0.9rem" }}>
            {currentUser()?.role === "MANAGEMENT" ? "Every case across all companies" : "Cases you are responsible for or created"}
          </p>
        </div>
        {can("create") && (<Link to="/legal-cases/cases/new" style={{ background: "var(--brand)", color: "#fff", fontWeight: 600, fontSize: "0.82rem", padding: "9px 16px", borderRadius: 8, textDecoration: "none" }}>
          + New Case
        </Link>)}
      </div>

      {error && <div style={{ color: "var(--st-risk)", marginBottom: 16 }}>{error}</div>}

      {summary && (<div className="lg-pair" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 24 }}>
          {tileDefs.map((t) => (<Link key={t.key} to={t.to} style={{
                    display: "block",
                    background: "var(--surface)",
                    border: "1px solid var(--line-soft)",
                    borderRadius: 10,
                    padding: "14px 16px",
                    textDecoration: "none",
                    color: "inherit",
                    cursor: "pointer",
                }}>
              <div className="mono" style={{ fontSize: "0.68rem", color: "var(--ink-faint)", textTransform: "uppercase" }}>
                {t.label}
              </div>
              <div className="mono tabnum" style={{ fontSize: "1.4rem", fontWeight: 600, marginTop: 6 }}>
                {t.format ? t.format(summary[t.key]) : summary[t.key]}
              </div>
            </Link>))}
        </div>)}

      {byCompany.length > 0 && (<div className="lg-table" style={cardStyle}>
          <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--line-soft)", fontWeight: 700, fontSize: "0.92rem" }}>
            By Company / Division
          </div>
          <div className="mono" style={{ display: "grid", gridTemplateColumns: "1.4fr 0.8fr 0.8fr 0.8fr 1fr 1fr 1fr", gap: 8, padding: "9px 18px", background: "var(--surface-2)" }}>
            <div style={theadCell}>Company</div>
            <div style={{ ...theadCell, textAlign: "right" }}>Total</div>
            <div style={{ ...theadCell, textAlign: "right" }}>Open</div>
            <div style={{ ...theadCell, textAlign: "right" }}>Closed</div>
            <div style={{ ...theadCell, textAlign: "right" }}>Receivable</div>
            <div style={{ ...theadCell, textAlign: "right" }}>Recovered</div>
            <div style={{ ...theadCell, textAlign: "right" }}>Outstanding</div>
          </div>
          {byCompany.map((r) => (<div key={r.companyId} style={{ display: "grid", gridTemplateColumns: "1.4fr 0.8fr 0.8fr 0.8fr 1fr 1fr 1fr", gap: 8, borderTop: "1px solid var(--line-soft)" }}>
              <Link to={`/legal-cases/cases?companyId=${r.companyId}`} style={{ ...rowLink, gridTemplateColumns: "1fr", padding: "10px 18px 10px 18px", fontWeight: 600, fontSize: "0.83rem", borderTop: "none" }}>
                {r.companyName}
              </Link>
              <Link to={`/legal-cases/cases?companyId=${r.companyId}`} style={{ ...rowLink, gridTemplateColumns: "1fr", padding: "10px 0", textAlign: "right", justifyContent: "end", fontSize: "0.8rem", borderTop: "none" }}>
                {r.totalCases}
              </Link>
              <Link to={`/legal-cases/cases?companyId=${r.companyId}&status=OPEN`} style={{ ...rowLink, gridTemplateColumns: "1fr", padding: "10px 0", textAlign: "right", justifyContent: "end", fontSize: "0.8rem", borderTop: "none" }}>
                {r.openCases}
              </Link>
              <Link to={`/legal-cases/cases?companyId=${r.companyId}&status=CLOSED_ALL`} style={{ ...rowLink, gridTemplateColumns: "1fr", padding: "10px 0", textAlign: "right", justifyContent: "end", fontSize: "0.8rem", borderTop: "none" }}>
                {r.closedCases}
              </Link>
              <Link to={`/legal-cases/reports?type=company-wise&companyId=${r.companyId}`} style={{ ...rowLink, gridTemplateColumns: "1fr", padding: "10px 0", textAlign: "right", justifyContent: "end", fontSize: "0.8rem", borderTop: "none" }}>
                {formatQAR(r.receivable)}
              </Link>
              <Link to={`/legal-cases/reports?type=recovery&companyId=${r.companyId}`} style={{ ...rowLink, gridTemplateColumns: "1fr", padding: "10px 0", textAlign: "right", justifyContent: "end", fontSize: "0.8rem", borderTop: "none" }}>
                {formatQAR(r.recovered)}
              </Link>
              <Link to={`/legal-cases/reports?type=outstanding-recovery&companyId=${r.companyId}`} style={{ ...rowLink, gridTemplateColumns: "1fr", padding: "10px 18px 10px 0", textAlign: "right", justifyContent: "end", fontSize: "0.8rem", borderTop: "none" }}>
                {formatQAR(r.outstanding)}
              </Link>
            </div>))}
        </div>)}

      {byAgeing.length > 0 && (<div className="lg-table lg-table-narrow" style={cardStyle}>
          <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--line-soft)", fontWeight: 700, fontSize: "0.92rem" }}>
            By Case Age
          </div>
          <div className="mono" style={{ display: "grid", gridTemplateColumns: "1.4fr 0.8fr 1fr 1fr 1fr", gap: 8, padding: "9px 18px", background: "var(--surface-2)" }}>
            <div style={theadCell}>Ageing Bucket</div>
            <div style={{ ...theadCell, textAlign: "right" }}>Cases</div>
            <div style={{ ...theadCell, textAlign: "right" }}>Receivable</div>
            <div style={{ ...theadCell, textAlign: "right" }}>Recovered</div>
            <div style={{ ...theadCell, textAlign: "right" }}>Outstanding</div>
          </div>
          {byAgeing.map((r) => (<Link key={r.bucketKey} to={`/legal-cases/cases?ageingBucket=${encodeURIComponent(r.bucketKey)}`} style={{ ...rowLink, gridTemplateColumns: "1.4fr 0.8fr 1fr 1fr 1fr" }}>
              <div style={{ fontSize: "0.83rem" }}>{r.label}</div>
              <div className="mono tabnum" style={{ fontSize: "0.8rem", textAlign: "right" }}>{r.count}</div>
              <div className="mono tabnum" style={{ fontSize: "0.8rem", textAlign: "right" }}>{formatQAR(r.receivable)}</div>
              <div className="mono tabnum" style={{ fontSize: "0.8rem", textAlign: "right" }}>{formatQAR(r.recovered)}</div>
              <div className="mono tabnum" style={{ fontSize: "0.8rem", textAlign: "right" }}>{formatQAR(r.outstanding)}</div>
            </Link>))}
        </div>)}
    </div>);
}
